import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  BookOpenIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  FolderIcon,
} from '@heroicons/react/24/outline';
import { kbApi, KBArticleListItem } from '../services/kbApi';
import { useAuthStore } from '../stores/authStore';
import KBLayout from '../components/KBLayout';
import KBCategoryManager from '../components/KBCategoryManager';

/**
 * Knowledge Base — list + search + filter chips. Operators see published
 * articles only; admin/superadmin also see drafts/archived via the status
 * filter.
 *
 * Search uses the server's Postgres FTS endpoint; client debounces input
 * by 300 ms so we don't fire on every keystroke.
 */
const KnowledgeBasePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const [articles, setArticles] = useState<KBArticleListItem[]>([]);
  const [categories, setCategories] = useState<{ category: string; count: number }[]>([]);
  const [tags, setTags] = useState<{ tag: string; count: number }[]>([]);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [activeTag, setActiveTag] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'' | 'draft' | 'published' | 'archived'>('');
  const [showCatManager, setShowCatManager] = useState(false);

  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await kbApi.list({
        q: q.trim() || undefined,
        category: activeCategory || undefined,
        tag: activeTag || undefined,
        status: (statusFilter || undefined) as any,
      });
      setArticles(res.articles);
    } catch (e: any) {
      toast.error(e?.message || 'Ошибка загрузки статей');
    } finally {
      setLoading(false);
    }
  }, [q, activeCategory, activeTag, statusFilter]);

  // Debounce search; fire immediately for filter changes.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchList, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [fetchList]);

  // Categories + tags. Bumping `taxoVersion` reloads them after the category
  // manager renames/deletes something.
  const [taxoVersion, setTaxoVersion] = useState(0);
  useEffect(() => {
    (async () => {
      try {
        const [c, t] = await Promise.all([kbApi.categories(), kbApi.tags()]);
        setCategories(c.categories);
        setTags(t.tags);
      } catch {/* silent — filter chips just won't show */}
    })();
  }, [taxoVersion]);

  const statusOptions = useMemo(
    () => [
      { v: '',          label: 'Все статусы' },
      { v: 'published', label: 'Опубликованные' },
      { v: 'draft',     label: 'Черновики' },
      { v: 'archived',  label: 'Архив' },
    ] as { v: '' | 'draft' | 'published' | 'archived'; label: string }[],
    []);

  return (
    <KBLayout>
    <div className="space-y-5">
      <div className="flex items-start sm:items-center justify-between gap-3 flex-col sm:flex-row">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <BookOpenIcon className="h-7 w-7 text-primary" />
            База знаний
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Инструкции, скрипты ответов, чек-листы для операторов.
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowCatManager(true)}
              className="inline-flex items-center gap-2 h-10 px-3 rounded-md border border-border bg-card hover:bg-accent transition-colors text-sm"
            >
              <FolderIcon className="h-4 w-4" />
              Категории
            </button>
            <button
              type="button"
              onClick={() => navigate('/kb/new')}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              <PlusIcon className="h-4 w-4" />
              Новая статья
            </button>
          </div>
        )}
      </div>

      {showCatManager && (
        <KBCategoryManager
          onClose={() => setShowCatManager(false)}
          onChanged={() => { setTaxoVersion((v) => v + 1); fetchList(); }}
        />
      )}

      {/* Search + status filter */}
      <div className="flex gap-2 flex-col sm:flex-row">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Поиск по статьям…"
            className="w-full h-10 pl-9 pr-3 rounded-md border border-border bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>
        {isAdmin && (
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="h-10 px-3 rounded-md border border-border bg-card text-sm text-foreground"
          >
            {statusOptions.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
        )}
      </div>

      {/* Filter chips: categories + tags */}
      {(categories.length > 0 || tags.length > 0) && (
        <div className="flex flex-wrap gap-2">
          {categories.length > 0 && (
            <>
              <Chip
                active={!activeCategory}
                onClick={() => setActiveCategory('')}
                label="Все категории"
              />
              {categories.map(c => (
                <Chip
                  key={c.category}
                  active={activeCategory === c.category}
                  onClick={() => setActiveCategory(activeCategory === c.category ? '' : c.category)}
                  label={`${c.category} (${c.count})`}
                />
              ))}
            </>
          )}
          {tags.slice(0, 12).map(t => (
            <Chip
              key={t.tag}
              tone="tag"
              active={activeTag === t.tag}
              onClick={() => setActiveTag(activeTag === t.tag ? '' : t.tag)}
              label={`#${t.tag}`}
            />
          ))}
        </div>
      )}

      {/* Article list */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground text-sm">Загрузка…</div>
      ) : articles.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-lg">
          <BookOpenIcon className="h-10 w-10 mx-auto text-muted-foreground/60 mb-3" />
          <p className="text-sm text-muted-foreground">
            {q || activeCategory || activeTag
              ? 'По запросу ничего не найдено'
              : 'Пока ни одной статьи' + (isAdmin ? '. Создайте первую — кнопка вверху.' : '.')}
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {articles.map(a => (
            <li key={a.id}>
              <Link
                to={`/kb/${a.id}`}
                className="block border border-border rounded-lg p-4 bg-card hover:bg-accent transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-base sm:text-lg font-semibold text-foreground truncate">
                        {a.title}
                      </h3>
                      {a.status !== 'published' && (
                        <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide ${
                          a.status === 'draft'
                            ? 'bg-amber-500/10 text-amber-600 border border-amber-500/20'
                            : 'bg-muted text-muted-foreground border border-border'
                        }`}>
                          {a.status === 'draft' ? 'черновик' : 'архив'}
                        </span>
                      )}
                    </div>
                    {a.excerpt && (
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {a.excerpt}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11px] text-muted-foreground">
                      {a.category && (
                        <span className="font-medium text-foreground/80">{a.category}</span>
                      )}
                      {a.tags.slice(0, 5).map(t => (
                        <span key={t} className="text-primary">#{t}</span>
                      ))}
                      <span className="inline-flex items-center gap-1">
                        <EyeIcon className="h-3 w-3" /> {a.views}
                      </span>
                      <span>·</span>
                      <span>
                        {a.author ? `${a.author.firstName} ${a.author.lastName}`.trim() : 'автор неизвестен'}
                      </span>
                      <span>·</span>
                      <span>{new Date(a.updatedAt).toLocaleDateString('ru-RU')}</span>
                    </div>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
    </KBLayout>
  );
};

const Chip: React.FC<{ label: string; active: boolean; onClick: () => void; tone?: 'tag' }>
  = ({ label, active, onClick, tone }) => (
    <button
      type="button"
      onClick={onClick}
      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
        active
          ? 'bg-primary text-primary-foreground border-primary'
          : tone === 'tag'
            ? 'bg-card text-primary border-border hover:border-primary/40'
            : 'bg-card text-foreground border-border hover:bg-accent'
      }`}
    >
      {label}
    </button>
  );

export default KnowledgeBasePage;
